import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSearch,
  FiBookOpen,
  FiFileText,
  FiMessageSquare,
  FiMail,
  FiPhone,
  FiChevronRight,
  FiExternalLink,
  FiShoppingBag,
  FiUsers,
  FiTag,
  FiSettings,
  FiGift
} from 'react-icons/fi';

const HelpSupport = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const adminGuides = [
    {
      title: 'Product Management',
      description: 'Complete guide for product operations',
      icon: FiShoppingBag,
      articles: [
        'Adding and editing products',
        'Managing product categories',
        'Setting up product variants',
        'Bulk product operations',
        'Product image guidelines'
      ],
    },
    {
      title: 'Order Management',
      description: 'Handle customer orders efficiently',
      icon: FiFileText,
      articles: [
        'Processing new orders',
        'Order status workflow',
        'Managing returns and refunds',
        'Generating order reports',
        'Handling order disputes'
      ],
    },
    {
      title: 'Customer Management',
      description: 'Customer data and interactions',
      icon: FiUsers,
      articles: [
        'Viewing customer profiles',
        'Managing customer groups',
        'Customer communication tools',
        'Handling customer feedback'
      ],
    },
    {
      title: 'Offers & Promotions',
      description: 'Create and manage promotional campaigns',
      icon: FiGift,
      articles: [
        'Creating discount offers',
        'Setting up promotional codes',
        'Scheduling campaigns',
        'Offer analytics and reports'
      ],
    },
    {
      title: 'Taxonomy & Categories',
      description: 'Organize products and content',
      icon: FiTag,
      articles: [
        'Creating category hierarchy',
        'Managing product tags',
        'Setting up filters',
        'Category-based pricing'
      ],
    },
    {
      title: 'System Settings',
      description: 'Configure system preferences',
      icon: FiSettings,
      articles: [
        'General settings configuration',
        'User roles and permissions',
        'Payment gateway setup',
        'Email notification settings',
        'System maintenance'
      ],
    }
  ];

  const supportChannels = [
    {
      icon: FiMessageSquare,
      title: 'Admin Support (WhatsApp)',
      description: 'Priority support for admin issues',
      status: '24/7 Available',
      statusColor: 'text-green-500',
      action: 'Contact Support',
      onClick: () => {window.open('https://wa.me/911234567890', '_blank')}
    },
    {
      icon: FiMail,
      title: 'Technical Support',
      description: 'For system-related queries',
      status: '4-8h response',
      statusColor: 'text-blue-500',
      action: 'Send Email',
      onClick: () => {window.location.href = 'mailto:tech.support@panchali.com'}
    },
    {
      icon: FiPhone,
      title: 'Emergency Support',
      description: 'For urgent technical issues',
      status: '24/7 Hotline',
      statusColor: 'text-red-500',
      action: 'Call Now',
      onClick: () => {window.location.href = 'tel:+911234567890'}
    },
  ];

  return (
    <>
      <Helmet>
        <title>Admin Help & Support | Panchali Boutique</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Section */}
        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-8 mb-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-white mb-4">
              Admin Control Panel Guide
            </h1>
            <p className="text-white text-lg opacity-90">
              Comprehensive documentation for managing your e-commerce platform
            </p>
          </div>
        </div>

        {/* Admin Guides Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {adminGuides.map((guide, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4 mb-4">
                <guide.icon className="h-8 w-8 text-indigo-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {guide.title}
                  </h3>
                  <p className="text-sm text-gray-500">{guide.description}</p>
                </div>
              </div>
              <ul className="space-y-2">
                {guide.articles.map((article, i) => (
                  <li key={i}>
                    <button className="flex items-center justify-between w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 rounded-lg group">
                      {article}
                      <FiChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Admin Support Channels */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Admin Support Channels
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportChannels.map((channel, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="border border-gray-100 rounded-xl p-6 hover:border-indigo-500 transition-colors cursor-pointer"
                onClick={channel.onClick}
              >
                <channel.icon className="h-8 w-8 text-indigo-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {channel.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{channel.description}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${channel.statusColor}`}>
                    {channel.status}
                  </span>
                  <button className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700">
                    {channel.action}
                    <FiExternalLink className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpSupport;


